/**
 * Hunt List page component
 */

import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal, ConfirmDialog, Pagination } from '../components/common';
import { HuntList, HuntForm } from '../components/hunts';
import { BottleForm } from '../components/bottles';
import { useHunts, useCreateHunt, useUpdateHunt, useDeleteHunt } from '../hooks/useHunts';
import { useCreateBottle } from '../hooks/useBottles';
import { useToast } from '../context/ToastContext';
import { DEFAULT_PAGE_SIZE } from '../utils/constants';
import './Hunt.css';

/**
 * Hunt list management page with CRUD operations
 * @returns {React.ReactElement} Page element
 */
export default function Hunt() {
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE;

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedHunt, setSelectedHunt] = useState(null);
  const [huntToDelete, setHuntToDelete] = useState(null);
  const [huntToConvert, setHuntToConvert] = useState(null);
  const [huntToAdd, setHuntToAdd] = useState(null);

  const { data, isLoading } = useHunts({ page, limit: pageSize });
  const hunts = data?.data || [];
  const totalCount = data?.count || 0;

  const createHunt = useCreateHunt();
  const updateHunt = useUpdateHunt();
  const deleteHunt = useDeleteHunt();
  const createBottle = useCreateBottle();

  const handleAddClick = () => {
    setSelectedHunt(null);
    setShowFormModal(true);
  };

  const handleEditClick = (item) => {
    setSelectedHunt(item);
    setShowFormModal(true);
  };

  const handleDeleteClick = (item) => {
    setHuntToDelete(item);
  };

  const handleFoundItClick = (item) => {
    setHuntToConvert(item);
  };

  const handleConvertConfirm = () => {
    setHuntToAdd(huntToConvert);
    setHuntToConvert(null);
  };

  const handleConvertSubmit = async (formData) => {
    try {
      await createBottle.mutateAsync(formData);
      await deleteHunt.mutateAsync(huntToAdd.id);
      addToast({ type: 'success', message: `"${huntToAdd.bottle_name}" added to your collection!` });
      setHuntToAdd(null);
    } catch (error) {
      addToast({
        type: 'error',
        message: error.message || 'Failed to add bottle to collection'
      });
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (selectedHunt) {
        await updateHunt.mutateAsync({ id: selectedHunt.id, updates: formData });
        addToast({ type: 'success', message: 'Hunt entry updated successfully' });
      } else {
        await createHunt.mutateAsync(formData);
        addToast({ type: 'success', message: 'Added to hunt list' });
      }
      setShowFormModal(false);
      setSelectedHunt(null);
    } catch (error) {
      addToast({
        type: 'error',
        message: error.message || 'Failed to save hunt entry'
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteHunt.mutateAsync(huntToDelete.id);
      addToast({ type: 'success', message: 'Hunt entry deleted' });
      setHuntToDelete(null);
    } catch (error) {
      addToast({
        type: 'error',
        message: error.message || 'Failed to delete hunt entry'
      });
    }
  };

  const isFormLoading = createHunt.isPending || updateHunt.isPending;
  const isConvertLoading = createBottle.isPending || deleteHunt.isPending;

  return (
    <div className="hunt-page">
      <div className="hunt-page__header">
        <h1 className="hunt-page__title">Hunt List</h1>
        <div className="hunt-page__actions">
          <button
            className="icon-btn icon-btn--add"
            onClick={handleAddClick}
            aria-label="Add Hunt Entry"
            title="Add Hunt Entry"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      <HuntList
        items={hunts}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        onFoundIt={handleFoundItClick}
        loading={isLoading}
      />

      {totalCount > 0 && (
        <Pagination
          currentPage={page}
          totalItems={totalCount}
          pageSize={pageSize}
          onPageChange={(p) => updateParams({ page: p === 1 ? '' : p })}
          onPageSizeChange={(size) => updateParams({ pageSize: size === DEFAULT_PAGE_SIZE ? '' : size, page: '' })}
        />
      )}

      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={selectedHunt ? 'Edit Hunt Entry' : 'Add to Hunt List'}
        size="lg"
      >
        <HuntForm
          hunt={selectedHunt}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowFormModal(false)}
          loading={isFormLoading}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!huntToDelete}
        onClose={() => setHuntToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Hunt Entry"
        message={`Are you sure you want to remove "${huntToDelete?.bottle_name}" from your hunt list?`}
        confirmText="Delete"
        loading={deleteHunt.isPending}
      />

      <ConfirmDialog
        isOpen={!!huntToConvert}
        onClose={() => setHuntToConvert(null)}
        onConfirm={handleConvertConfirm}
        title="Found It!"
        message={`Add "${huntToConvert?.bottle_name}" to your collection? You'll be able to fill in the details before it's saved, and it will be removed from your hunt list.`}
        confirmText="Yes, Add to Collection"
      />

      <Modal
        isOpen={!!huntToAdd}
        onClose={() => setHuntToAdd(null)}
        title="Add to Collection"
        size="lg"
      >
        <BottleForm
          bottle={huntToAdd ? {
            bottle_name: huntToAdd.bottle_name,
            type: huntToAdd.type || '',
            distillery_id: huntToAdd.distillery_id || '',
            notes: huntToAdd.notes || '',
            status: 'Unopened',
            size: '750ml',
            single_barrel: false,
            bottled_in_bond: false,
            cask_strength: false,
            small_batch: false,
            double_barreled: false,
          } : null}
          isClone={true}
          submitLabel="Add to Collection"
          onSubmit={handleConvertSubmit}
          onCancel={() => setHuntToAdd(null)}
          loading={isConvertLoading}
        />
      </Modal>
    </div>
  );
}
